import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormGetValues, UseFormUnregister } from 'react-hook-form';

import EditModeCellWrapper from './EditModeCellWrapper';
import Input from '../RHFFieldWrappers/Input/Input';
import InputField from '../RHFFieldWrappers/Input/InputFieldCell';
import type { InputCellType, InputSharedProps } from '../RHFTableCellComponents/Input/types';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues> =
	UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		type: InputCellType;
		rhfId: string | number;
		onChange?: (name: string) => void;
		unregister: UseFormUnregister<TFieldValues>;
		getValues: UseFormGetValues<TFieldValues>;
		fieldsLength: number;
		readonly?: boolean;
		readonlyFieldClassName?: string;
		className?: string;
	} & InputSharedProps;

function InputCellRenderer<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const {
		type,
		rhfId,
		control,
		name,
		defaultValue,
		onChange,
		unregister,
		getValues,
		fieldsLength,
		readonly,
		readonlyFieldClassName,
		prefixGlyphs,
		suffixGlyphs,
		className,
	} = props;

	return (
		<EditModeCellWrapper name={name} readonly={readonly} >
			{(shouldBeRegistered, exitEditMode) =>
				shouldBeRegistered ? (
					<InputField
						className={className}
						control={control}
						defaultValue={defaultValue}
						fieldsLength={fieldsLength}
						key={name}
						name={name}
						onBlur={exitEditMode}
						onChange={onChange}
						prefixGlyphs={prefixGlyphs}
						rhfId={rhfId}
						suffixGlyphs={suffixGlyphs}
						type={type}
						unregister={unregister}
					/>
				) : (
					<Input
						className={className}
						control={control}
						defaultValue={defaultValue}
						getValues={getValues}
						key={name}
						name={name}
						prefixGlyphs={prefixGlyphs}
						readonly={readonly}
						readonlyFieldClassName={readonlyFieldClassName}
						rhfId={rhfId}
						suffixGlyphs={suffixGlyphs}
						type={type}
					/>
				)
			}
		</EditModeCellWrapper>
	);
}

export default React.memo(InputCellRenderer) as typeof InputCellRenderer;
